import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './JobProgressPopup.css';
import { makeRequest, handleDownloadFile } from '@utils/helpers';
import { Empty, message, Popconfirm, Progress, Spin, Tooltip } from 'antd';
import { getReportsFromJobsReq, downloadReportFromJobsReq } from '../../modules/reports/allReports/requests';
import { ClockCircleOutlined, CloseOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, LoadingOutlined } from '@ant-design/icons';
import { decryptLocalData, pusherInitializer } from '@comps/commonFunctions';
import { useSelector } from 'react-redux';
import { cancelPayrollJobReq } from '../../modules/common/requests';
import { convertTimeTotz } from '../commonFunctions';
import { useNavigate } from 'react-router-dom';

const isActiveReportJob = (item) => item?.status !== 'failed' && !item?.is_downloaded && Number(item?.progress ?? 0) < 100;
const hasActiveBatchJobs = (items = []) => items.some((item) => Number(item?.progress ?? 0) < 100);

const JobProgressPopup = ({ standalone = false }) => {
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.authUser);
  const selectedNotificationRef = useRef(null);
  const popupRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const jobBatches = useMemo(() => [], []);

  const totalItems = dataSource.length + jobBatches.length;
  const hasActiveJobs = useMemo(() => {
    const hasActiveReports = dataSource.some(isActiveReportJob);
    return hasActiveReports || hasActiveBatchJobs(jobBatches);
  }, [dataSource, jobBatches]);

  const onSuccessReports = useCallback((data) => {
    setDataSource(Array.isArray(data?.jobs) ? data.jobs : []);
  }, []);

  const onErrorReports = useCallback((err) => {
    console.log(err);
  }, []);

  const getJobStatus = useCallback(() => {
    makeRequest(setLoader, getReportsFromJobsReq, { employment_id: authUser?.employment_id }, onSuccessReports, onErrorReports);
  }, [authUser?.employment_id, onErrorReports, onSuccessReports]);

  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user || !user.tenant) {
      return undefined;
    }

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    const handleRealtimeUpdate = (event = {}) => {
      if (event.employment_id && authUser?.employment_id && String(event.employment_id) !== String(authUser.employment_id)) {
        return;
      }

      getJobStatus();
      setIsOpen(true);
    };

    channel.bind('report-downloaded-event', handleRealtimeUpdate);
    channel.bind('batch-progress-event', handleRealtimeUpdate);

    return () => {
      channel.unbind('report-downloaded-event', handleRealtimeUpdate);
      channel.unbind('batch-progress-event', handleRealtimeUpdate);
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [authUser, getJobStatus]);

  useEffect(() => {
    getJobStatus();

    const timer = setInterval(() => {
      if (hasActiveJobs) {
        getJobStatus();
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [getJobStatus, hasActiveJobs]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const onView = (item) => {
    selectedNotificationRef.current = item;
    makeRequest(setLoader, downloadReportFromJobsReq, { id: item.id, employment_id: authUser?.employment_id }, onSuccessView, onErrorView);
  };

  const onSuccessView = (err, res) => {
    const selectedNotification = selectedNotificationRef.current;

    if (res.code === undefined && selectedNotification) {
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf';
      handleDownloadFile(`${selectedNotification.title}.${fileExtension}`, res);
      return;
    }

    message.error(res.message);
  };

  const onErrorView = (err, res) => {
    const selectedNotification = selectedNotificationRef.current;

    if (res.code === undefined && selectedNotification) {
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf';
      handleDownloadFile(`${selectedNotification.title}.${fileExtension}`, res);
      return;
    }

    message.error(res.message);
  };

  const onCancelBatch = (item) => {
    const payrollId = item?.options?.data?.param?.payroll_id;

    if (payrollId) {
      makeRequest(setLoader, cancelPayrollJobReq, payrollId, getJobStatus, null);
    }
  };

  const getItemTitle = (item) => item?.title || item?.name || item?.options?.data?.class?.split('\\').pop() || 'Background job';

  const getReportKind = (item) => {
    if (item?.status === 'failed') {
      return item?.is_scheduled ? 'Scheduled report failed' : 'Report failed';
    }

    if (item?.is_scheduled) {
      return item?.is_downloaded ? 'Scheduled report ready' : 'Scheduled report';
    }

    return item?.is_downloaded ? 'Report ready' : 'Report file';
  };

  const getItemTime = (item) => {
    const timestamp = item?.created_at || item?.updated_at;
    return timestamp ? convertTimeTotz(timestamp).format('YYYY-MM-DD hh:mm a') : 'In progress';
  };

  const renderReportStatus = (item) => {
    if (item?.status === 'failed') {
      return (
        <Tooltip title={ item?.error_message || 'Report generation failed' }>
          <span className="job-progress-popup__icon-button is-danger">
            <CloseOutlined />
          </span>
        </Tooltip>
      );
    }

    if (item?.is_downloaded) {
      return (
        <Tooltip title="Download report file">
          <button type="button" className="job-progress-popup__icon-button is-success" onClick={ () => onView(item) }>
            <DownloadOutlined />
          </button>
        </Tooltip>
      );
    }

    if (Number(item?.progress) === 100) {
      return <Spin indicator={ <LoadingOutlined spin /> } />;
    }

    return <Progress type="circle" percent={ Number(item?.progress ?? 0) } size={ 34 } strokeColor="var(--brand)" />;
  };

  const renderBatchStatus = (item) => {
    const payrollJob = item?.options?.data?.class === 'App\\Jobs\\PayrollManagement\\RunPayroll';

    return (
      <div className="job-progress-popup__batch-actions">
        <Progress type="circle" percent={ Number(item?.progress ?? 0) } size={ 34 } strokeColor="var(--green)" />
        { payrollJob && (
          <Popconfirm title="Are you sure to cancel this task?" onConfirm={ () => onCancelBatch(item) }>
            <button type="button" className="job-progress-popup__icon-button is-danger">
              <CloseOutlined />
            </button>
          </Popconfirm>
        ) }
      </div>
    );
  };

  const notificationItems = useMemo(() => {
    const reportItems = dataSource.map((item) => ({
      key: `report-${item.id}`,
      item,
      type: 'report',
    }));

    const batchItems = jobBatches.map((item, index) => ({
      key: `batch-${item.id || index}`,
      item,
      type: 'batch',
    }));

    return [...batchItems, ...reportItems];
  }, [dataSource, jobBatches]);

  const onTogglePopup = () => {
    getJobStatus();
    setIsOpen((current) => !current);
  };

  return (
    <div
      ref={ popupRef }
      className={ `job-progress-popup${standalone ? ' is-standalone' : ''}${isOpen ? ' is-open' : ''}` }
    >
      <Tooltip title="Progress center" placement="bottom" open={ isOpen ? false : undefined }>
        <button
          type="button"
          className={ `job-progress-popup__trigger${hasActiveJobs ? ' has-activity' : ''}` }
          onClick={ onTogglePopup }
          aria-label="Toggle job progress"
        >
          <ClockCircleOutlined />
          { totalItems > 0 && <span className="job-progress-popup__badge">{ totalItems }</span> }
        </button>
      </Tooltip>

      { isOpen && (
        <div className="job-progress-popup__panel" role="dialog" aria-label="Progress Center">
          <div className="job-progress-popup__panel-header">
            <div className="job-progress-popup__header-icon">
              <FileTextOutlined />
            </div>
            <div className="job-progress-popup__header-copy">
              <div className="job-progress-popup__eyebrow">Background jobs</div>
              <div className="job-progress-popup__title">Progress center</div>
            </div>
            <button
              type="button"
              className="job-progress-popup__close-button"
              onClick={ () => setIsOpen(false) }
              aria-label="Close job progress"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="job-progress-popup__panel-summary">
            <span>{ totalItems } item{ totalItems === 1 ? '' : 's' }</span>
            <span>{ hasActiveJobs ? 'Live updates on' : 'All caught up' }</span>
          </div>

          <div className="job-progress-popup__list">
            { !notificationItems.length && (
              <div className="job-progress-popup__empty">
                <Empty
                  image={ Empty.PRESENTED_IMAGE_SIMPLE }
                  description="No job progress to show right now."
                />
              </div>
            ) }

            { notificationItems.map(({ key, item, type }) => (
              <div className="job-progress-popup__item" key={ key }>
                <div className={ `job-progress-popup__item-icon is-${item.path?.includes('.xlsx') ? 'xlsx' : item.path?.includes('.pdf') ? 'pdf' : 'doc'}` }>

                  { !item.path ? <FileTextOutlined /> : item.path.includes('.xlsx') ? <FileExcelOutlined /> : item.path.includes('.pdf') ? <FilePdfOutlined /> : <FileTextOutlined />
                  }
                </div>

                <div className="job-progress-popup__item-content">
                  <div className="job-progress-popup__item-title">{ getItemTitle(item) }</div>
                  <div className="job-progress-popup__item-meta">
                    <span>{ item?.size || item?.file_size || (type === 'batch' ? 'Background job' : getReportKind(item)) }</span>
                    <span>{ getItemTime(item) }</span>
                  </div>
                </div>

                <div className="job-progress-popup__item-status">
                  { type === 'batch' ? renderBatchStatus(item) : renderReportStatus(item) }
                </div>
              </div>
            )) }
          </div>

          <div className="job-progress-popup__footer">
            { loader ? (
              <>
              <Spin size="small" />
              <span>Refreshing latest status...</span>
              </>
            ) : <span>{ hasActiveJobs ? 'Watching running jobs' : 'Latest status loaded' }</span> }
            <button type="button" className="job-progress-popup__footer-link" onClick={ () => navigate('/reports/all-reports') }>
              View all reports
            </button>
          </div>
        </div>
      ) }
    </div>
  );
};

export default JobProgressPopup;
